/*    
<summary>
  This functional component "AddUpdateCustomerSetting" defines the props like initial values and functions for the child form component.
  Developer:Mohammad Saquib Khan, Created Date:04-June-2024
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { initialPageLimit } from "../../../../constants/pageLimit.constant";
import { useStore } from "../../../../contexts/store-provider";
import IPageInfo from "../../../../models/ICommon";
import { ISorting } from "../../../../models/ISorting";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import { formatMessage } from "../../../../translations/format-message";
import TimeListView from "./components/time-list-view";
import AddTimeSettingForm from "./forms/add-time-form";

interface IProps {
  id: number;
  modalClosed: () => void;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
}

const TimeSettings = (props: IProps) => {
  const [isDetailsVisible, setIsDetailsVisible] = useState<boolean>(false);
  const { masterManagementStore } = useStore();
  const {
    addUpdateTimeSettingState,
    GetAllTimeService,
    getAllTimeList,
    AddTimeService,
    UpdateTimeService,
    DeleteTimeService,
    GetTimeByIdService,
    deleteTimeSettingState,
    getByIdTimeSettingState,
    getTimeDetail,
    timeSettingState,
    resetGetAllTimeSettingState,
    resetGetByIdTimeSettingState,
    resetdDeleteTimeSettingState,
    resetAddUpdateTimeSettingState,
  } = masterManagementStore;
  // const initialValues: IAddCustomerRate = addCustomerRateState;
  const initialValues = getTimeDetail;

  const { id, modalClosed, showDeletePopup, deletePopupToggleHandler } = props;

  const onModalClosed = useCallback(() => {
    modalClosed();
    resetAddUpdateTimeSettingState();
    resetdDeleteTimeSettingState();
    resetGetAllTimeSettingState();
    resetGetByIdTimeSettingState();
  }, [modalClosed]);

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const handleVisibility = () => {
    setIsDetailsVisible((e) => !e);
  };
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [editableId, setEditableId] = useState<number>(-1);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<number>(initialPageLimit);
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });
  const onPageChanged = useCallback((pageNumber: number) => {
    setPageInfo((pageInfo) => ({ ...pageInfo, currentPage: pageNumber }));
  }, []);
  const onPageLimitChanged = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    let value = parseInt(evt.target.value);
    setPageLimit(value);
  };
  const [sorting, setSorting] = useState<ISorting>({
    column: "userName",
    orderBy: "asc",
  });
  const [isApiCalled, setIsApiCalled] = useState<boolean>(false);

  const callGetTimeList = useCallback(() => {
    setDisablePagination(true);
    GetAllTimeService();
    setIsApiCalled(true);
  }, [GetAllTimeService]);

  useEffect(() => {
    setPageInfo((pageInfo) => ({
      ...pageInfo,
      // totalPages: customerRateList?.TotalPages,
      totalPages: 1,
    }));
    setDisablePagination(false);
  }, [getAllTimeList]);
  // }, [getAllTimeList, customerRateList.TotalPages]);

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const onSubmitForm = (values: any) => {
    let finalValues = {
      ...values,
    };
    if (!(values.Id && values.Id > 0)) {
      AddTimeService(finalValues);
    } else {
      UpdateTimeService(values.Id, finalValues);
    }
  };

  const deleteHandler = () => {
    DeleteTimeService(selectedId);
    deletePopupToggleHandler();
    setEditableId(-1);
    resetAddUpdateTimeSettingState();
  };

  const editHandler = (row: { Id: number }) => {
    setEditableId(row.Id);
    GetTimeByIdService(row.Id);
  };

  useEffect(() => {
    callGetTimeList();
  }, [callGetTimeList]);

  useEffect(() => {
    if (addUpdateTimeSettingState.success) {
      if (editableId > 0) {
        toast.success(formatMessage("time_setting_update_success"));
      } else {
        toast.success(formatMessage("time_setting_add_success"));
      }
      callGetTimeList();
      resetAddUpdateTimeSettingState();
      setEditableId(-1);
    }
  }, [
    addUpdateTimeSettingState.success,
    callGetTimeList,
    resetAddUpdateTimeSettingState,
    editableId,
  ]);

  useEffect(() => {
    if (deleteTimeSettingState.success) {
      resetdDeleteTimeSettingState();
      toast.success(formatMessage("time_setting_delete_success"));
      callGetTimeList();
    }
  }, [
    deleteTimeSettingState.success,
    callGetTimeList,
    resetdDeleteTimeSettingState,
  ]);

  return (
    <React.Fragment>
      {(getByIdTimeSettingState.inProgress ||
        addUpdateTimeSettingState.inProgress ||
        deleteTimeSettingState.inProgress ||
        timeSettingState.inProgress) && (
        <FormLoader
          loading={
            getByIdTimeSettingState.inProgress ||
            addUpdateTimeSettingState.inProgress ||
            deleteTimeSettingState.inProgress ||
            timeSettingState.inProgress
          }
        />
      )}

      {showDeletePopup && (
        <DeletePopup
          title="delete_time"
          modalSubmit={deleteHandler}
          modalClosed={deletePopupToggleHandler}
          isLoading={deleteTimeSettingState.inProgress}
          message="delete_time_confirm"
        />
      )}

      <AddTimeSettingForm
        submitHandler={onSubmitForm}
        addUpdateInProgress={addUpdateTimeSettingState.inProgress}
        actionSuccess={addUpdateTimeSettingState.success}
        initialValues={initialValues}
        id={editableId}
        onCancelEdit={() => {
          setEditableId(-1);
          resetAddUpdateTimeSettingState();
        }}
      />
      <TimeListView
        setVisiblity={handleVisibility}
        id={id}
        showDeletePopup={showDeletePopup}
        deleteToggleHandler={deletePopupToggleHandler}
        onPageLimitChanged={onPageLimitChanged}
        pageLimit={pageLimit}
        pageInfo={pageInfo}
        onPageChanged={onPageChanged}
        disablePagination={disablePagination}
        sorting={sorting}
        handleSortingChange={handleSortingChange}
        setSelectedId={setSelectedId}
        selectedId={selectedId}
        editHandler={editHandler}
        setIsRefreshPage={setIsRefreshPage}
      />
    </React.Fragment>
  );
};

export default DeletePopupHOC(observer(TimeSettings));
